import React, { useEffect, useState } from "react";
import Layout, { PageHeader } from "../components/Layout";
import api, { formatApiError, MODULES } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { UserPlus, KeyRound, Users, Trash2, Pencil } from "lucide-react";
import { toast } from "sonner";
import { UserActivityReport } from "../components/UserActivityReport";
import { CashbookLockReport } from "../components/CashbookLockReport";

const field = "w-full h-10 px-3 rounded-md border border-input bg-card text-sm focus:outline-none focus:ring-2 focus:ring-ring/40";
const label = "block text-sm font-medium mb-1.5";
const ROLES = ["cashier", "accountant", "viewer", "admin"];
const roleColors = {
  admin: "bg-[hsl(var(--positive))]/15 text-[hsl(var(--positive))]",
  accountant: "bg-primary/10 text-primary",
  cashier: "bg-accent/15 text-accent",
  viewer: "bg-muted text-muted-foreground",
};

const emptyForm = () => ({ email: "", password: "", name: "", roles: ["cashier"], restrict: false, module_access: [] });

export default function Team() {
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [pwUser, setPwUser] = useState(null);
  const [pw, setPw] = useState("");

  const load = async () => setUsers((await api.get("/company/users")).data);
  useEffect(() => { load(); }, []);

  const toggleRole = (r) => setForm((f) => ({ ...f, roles: f.roles.includes(r) ? f.roles.filter((x) => x !== r) : [...f.roles, r] }));
  const toggleModule = (k) => setForm((f) => ({ ...f, module_access: f.module_access.includes(k) ? f.module_access.filter((x) => x !== k) : [...f.module_access, k] }));

  const openNew = () => { setEdit(null); setForm(emptyForm()); setOpen(true); };
  const openEdit = (u) => {
    setEdit(u.id);
    setForm({
      email: u.email, password: "", name: u.name, roles: u.tenant_roles || ["cashier"],
      restrict: Array.isArray(u.module_access), module_access: u.module_access || [],
    });
    setOpen(true);
  };

  const save = async () => {
    try {
      if (!form.name || form.roles.length === 0) return toast.error("Name and at least one role required");
      const module_access = form.restrict ? form.module_access : null;
      if (edit) {
        const payload = { name: form.name, roles: form.roles, module_access };
        if (form.password) payload.password = form.password;
        await api.put(`/company/users/${edit}`, payload);
        toast.success("User updated");
      } else {
        if (!form.email || !form.password) return toast.error("Email and password required");
        await api.post("/company/users", { email: form.email, password: form.password, name: form.name, roles: form.roles, module_access });
        toast.success("User created");
      }
      setOpen(false); setForm(emptyForm()); setEdit(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const del = async (u) => {
    if (!window.confirm(`Delete ${u.name}? They will lose access immediately.`)) return;
    try { await api.delete(`/company/users/${u.id}`); toast.success("User deleted"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const savePw = async () => {
    try {
      if (pw.length < 6) return toast.error("Password must be at least 6 characters");
      await api.put(`/company/users/${pwUser.id}/password`, { new_password: pw });
      toast.success(`Password reset for ${pwUser.name}`);
      setPwUser(null); setPw("");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <Layout>
      <div className="p-8 max-w-5xl mx-auto">
        <PageHeader title="Users" subtitle="Add your team, assign roles, and control exactly which screens each person can access.">
          <button data-testid="add-team-user-btn" onClick={openNew} className="h-10 px-4 rounded-md bg-primary text-primary-foreground text-sm font-medium flex items-center gap-2 hover:bg-primary/90 transition-colors">
            <UserPlus size={16} /> New User
          </button>
        </PageHeader>

        {users.length === 0 ? (
          <div className="rounded-xl border border-border bg-card p-12 text-center text-muted-foreground">
            <Users className="mx-auto mb-3 opacity-40" size={32} /> No users yet.
          </div>
        ) : (
          <div className="rounded-xl border border-border bg-card overflow-hidden animate-fade-up">
            <div className="grid grid-cols-[1.4fr_auto_auto] gap-4 px-5 py-3 bg-muted text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              <span>User</span><span>Roles & Access</span><span></span>
            </div>
            <div className="divide-y divide-border">
              {users.map((u) => (
                <div key={u.id} data-testid={`team-row-${u.id}`} className="grid grid-cols-[1.4fr_auto_auto] gap-4 px-5 py-4 items-center">
                  <div>
                    <div className="font-medium">{u.name}{u.platform_role === "superadmin" && <span className="ml-2 text-xs text-muted-foreground">(superadmin)</span>}</div>
                    <div className="text-xs text-muted-foreground num">{u.email}</div>
                  </div>
                  <div className="flex flex-col items-end gap-1 max-w-[280px]">
                    <div className="flex flex-wrap gap-1 justify-end">
                      {(u.tenant_roles || []).map((r) => (
                        <span key={r} className={`text-xs px-2 py-1 rounded-full capitalize font-medium ${roleColors[r] || roleColors.viewer}`}>{r}</span>
                      ))}
                      {u.platform_role === "superadmin" && <span className="text-xs px-2 py-1 rounded-full font-medium bg-primary text-primary-foreground">superadmin</span>}
                    </div>
                    <div className="text-[11px] text-muted-foreground">{Array.isArray(u.module_access) ? `${u.module_access.length} screens` : "All screens"}</div>
                  </div>
                  <div className="flex items-center gap-3 justify-end">
                    {u.platform_role !== "superadmin" && (
                      <>
                        <button data-testid={`team-edit-${u.id}`} onClick={() => openEdit(u)} title="Edit" className="text-muted-foreground hover:text-primary transition-colors"><Pencil size={16} /></button>
                        <button data-testid={`team-reset-${u.id}`} onClick={() => { setPwUser(u); setPw(""); }} title="Reset password" className="text-muted-foreground hover:text-primary transition-colors"><KeyRound size={16} /></button>
                        <button data-testid={`team-del-${u.id}`} onClick={() => del(u)} title="Delete" className="text-muted-foreground hover:text-destructive transition-colors"><Trash2 size={16} /></button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setForm(emptyForm()); setEdit(null); } }}>
          <DialogContent className="bg-card max-h-[92vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="font-display text-2xl">{edit ? "Edit User" : "New User"}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><label className={label}>Full name</label>
                <input data-testid="team-user-name" className={field} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className={label}>Email</label>
                  <input data-testid="team-user-email" type="email" className={field} value={form.email} disabled={!!edit} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
                <div><label className={label}>{edit ? "New password (optional)" : "Password"}</label>
                  <input data-testid="team-user-password" className={field} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder={edit ? "leave blank to keep" : "min 6 chars"} /></div>
              </div>
              <div>
                <label className={label}>Roles <span className="text-muted-foreground font-normal">(multiple allowed)</span></label>
                <div className="grid grid-cols-2 gap-2">
                  {ROLES.map((r) => (
                    <button key={r} type="button" data-testid={`team-role-${r}`} onClick={() => toggleRole(r)}
                      className={`h-10 rounded-md text-sm capitalize border transition-colors ${form.roles.includes(r) ? "bg-primary text-primary-foreground border-primary" : "bg-card border-input hover:bg-muted"}`}>{r}</button>
                  ))}
                </div>
              </div>
              <div className="rounded-md border border-border p-3">
                <label className="flex items-center gap-2 text-sm font-medium cursor-pointer">
                  <input data-testid="team-restrict-toggle" type="checkbox" checked={form.restrict} onChange={(e) => setForm({ ...form, restrict: e.target.checked })} />
                  Restrict screen access
                </label>
                {form.restrict ? (
                  <>
                    <p className="text-xs text-muted-foreground mt-1 mb-2">Pick the screens this user can open. Admins always see everything.</p>
                    <button type="button" data-testid="team-gatepass-only" onClick={() => setForm({ ...form, module_access: ["gatepass"] })}
                      className="mb-2 h-9 w-full rounded-md text-xs font-semibold border border-primary text-primary hover:bg-primary/10 transition-colors">Gate Pass only (nothing else, not even Dashboard)</button>
                    <button type="button" data-testid="team-manager-preset" onClick={() => setForm({ ...form, roles: ["viewer"], module_access: ["gatepass", "credit", "storeinvoices"] })}
                      className="mb-2 h-9 w-full rounded-md text-xs font-semibold border border-accent text-accent hover:bg-accent/10 transition-colors">Manager (view &amp; download only: Gate Pass, Credit Accounts, Store Invoices)</button>
                    <div className="grid grid-cols-2 gap-2">
                      {MODULES.filter((m) => m.key !== "dashboard").map((m) => (
                        <button key={m.key} type="button" data-testid={`team-mod-${m.key}`} onClick={() => toggleModule(m.key)}
                          className={`h-9 rounded-md text-xs border transition-colors ${form.module_access.includes(m.key) ? "bg-accent text-accent-foreground border-accent" : "bg-card border-input hover:bg-muted"}`}>{m.label}</button>
                      ))}
                    </div>
                  </>
                ) : <p className="text-xs text-muted-foreground mt-1">This user can access all screens allowed by their role.</p>}
              </div>
            </div>
            <DialogFooter>
              <button data-testid="team-user-save" onClick={save} className="h-10 px-5 rounded-md bg-accent text-accent-foreground font-medium hover:bg-accent/90 transition-colors">{edit ? "Save changes" : "Create user"}</button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={!!pwUser} onOpenChange={(o) => !o && setPwUser(null)}>
          <DialogContent className="bg-card">
            <DialogHeader><DialogTitle className="font-display text-2xl">Reset password · {pwUser?.name}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><label className={label}>New password</label>
                <input data-testid="team-new-password" type="text" className={field} value={pw} onChange={(e) => setPw(e.target.value)} placeholder="At least 6 characters" /></div>
              <p className="text-xs text-muted-foreground">Share the new password securely. They can change it later via "Forgot password".</p>
            </div>
            <DialogFooter>
              <button data-testid="team-save-password" onClick={savePw} className="h-10 px-5 rounded-md bg-accent text-accent-foreground font-medium hover:bg-accent/90 transition-colors">Set password</button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        <UserActivityReport users={users} />
        <CashbookLockReport />
      </div>
    </Layout>
  );
}
